<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    const dispatch = createEventDispatcher()
    import { faAngleLeft, faEdit, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { PaymentLink } from '../../modules/paymentsApi/payment-links/payment-link.entity'
    import type { CreatePaymentLinkDto } from '../../modules/paymentsApi/payment-links/dto/create-payment-link.dto'
    import type { Gateway } from '../../modules/paymentsApi/gateways/gateway.entity'
    import { PurchaseType, PurchaseTypeFriendly, isSubscriptionType } from '../../modules/paymentsApi/purchases/purchase.enum'
    import { UpdatePaymentLinkDto } from '../../modules/paymentsApi/payment-links/dto/update-payment-link.dto'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import { PaymentMethods, PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import ZinputTitle from '../_common/z/zinput-title.svelte'
    // ##############################################################################################################

    export let form: boolean = false
    export let paymentLinkId: string = null
    let entity: PaymentLink = new PaymentLink()

    let purchaseTotalLabel = 'Valor total'

    $: {
        if (entity?.purchaseType === PurchaseType.recurring_continuous) {
            purchaseTotalLabel = 'Valor mensal'
        } else {
            purchaseTotalLabel = 'Valor total'
        }
    }

    async function editEntity(id: string) {
        try {
            show = true
            entity = new PaymentLink()
            entity = await window.api.payments.paymentLinks.getOne(id)
            console.log('payment-link', entity)
            // o toggle reflete o que o cadastro já tem: sem entrada gravada ele abre desligado
            comEntrada = !!entity.purchaseEntryValue
            step = 2
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        entity = new PaymentLink()
        comEntrada = false
        step = 2
    }

    function evaluateDto(dto: Partial<PaymentLink>) {
        if (!dto.gatewayId) {
            window.error('Informe o gateway de pagamento')
            return false
        }
        if (!dto.purchaseDescription) {
            window.error('Informe a descrição da compra')
            return false
        }
        if (!dto.purchaseType) {
            window.error('Informe o método de cobrança')
            return false
        }
        if (dto.purchaseType !== PurchaseType.recurring_continuous && !dto.purchaseTotalInstallments) {
            window.error('Informe a quantidade máxima de parcelas permitida para este link')
            return false
        }
        if (!dto.purchaseQuantity) {
            window.error('Informe a quantidade de itens a serem adquiridos com este link')
            return false
        }
        if (!dto.purchaseTotalValue) {
            window.error('Informe o valor a ser cobrado com este link')
            return false
        }
        if (dto.purchaseEntryValue) {
            if (dto.purchaseEntryValue >= dto.purchaseTotalValue) {
                window.error('A entrada precisa ser menor que o valor total')
                return false
            }
            // com um vencimento só não sobra parcela depois da entrada — isso é cobrança única
            if (dto.purchaseTotalInstallments < 2) {
                window.error('Para ter entrada, o carnê precisa de pelo menos 2 vencimentos')
                return false
            }
        }
        return true
    }

    async function createEntity() {
        try {
            show = true
            let dto: CreatePaymentLinkDto = {
                gatewayId: entity.gatewayId,
                paymentMethodsAllowed: entity.paymentMethodsAllowed,
                purchaseDescription: entity.purchaseDescription,
                purchaseQuantity: 1,
                purchaseTotalInstallments: entity.purchaseTotalInstallments,
                purchaseTotalValue: entity.purchaseTotalValue,
                // entrada só existe no carnê e só quando ligada; nos demais casos vai nula, e a
                // divisão é a de sempre
                purchaseEntryValue: comEntrada ? entity.purchaseEntryValue || null : null,
                purchaseType: entity.purchaseType,
                referenceId: null,
                category: 'link',
                purchaseInterval: 1,
            }
            if (semParcelamento(entity.purchaseType, entity.paymentMethodsAllowed)) {
                dto.purchaseTotalInstallments = 1
            }
            if (!evaluateDto(dto)) return
            entity = await window.api.payments.paymentLinks.create(dto)
            window.api.users.statistics.tick('pay.link.create')
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function sendClose() {
        dispatch('close')
    }

    async function updateEntity() {
        try {
            show = true
            let dto: UpdatePaymentLinkDto = {
                gatewayId: entity.gatewayId,
                paymentMethodsAllowed: entity.paymentMethodsAllowed,
                purchaseDescription: entity.purchaseDescription,
                purchaseQuantity: 1,
                purchaseTotalInstallments: entity.purchaseTotalInstallments,
                purchaseTotalValue: entity.purchaseTotalValue,
                // entrada só existe no carnê e só quando ligada; nos demais casos vai nula, e a
                // divisão é a de sempre
                purchaseEntryValue: comEntrada ? entity.purchaseEntryValue || null : null,
                purchaseType: entity.purchaseType,
                category: 'link',
            }
            await window.api.payments.paymentLinks.update(entity.paymentLinkId, dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (entity.paymentLinkId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.payments.paymentLinks.getAll({ category: 'link' })
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.payments.paymentLinks.remove(entity.paymentLinkId)
            await loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function showEntities() {
        step = 1
    }

    let show = false
    let step = 1
    let entities: PaymentLink[] = []

    let gateways: Gateway[] = []
    let gateway: Gateway = null
    async function loadGateways() {
        gateways = await window.api.payments.gateways.getAll()
        if (gateways.length === 1) {
            gateway = gateways[0]
        }
    }

    onMount(async () => {
        await loadGateways()
        await loadEntities()
        if (form) {
            if (paymentLinkId) {
                await editEntity(paymentLinkId)
            } else {
                newEntity()
            }
        }
    })

    // rótulo único, no enum: as três telas diziam a mesma coisa com palavras diferentes
    function friendlyType(type: PurchaseType) {
        return PurchaseTypeFriendly[type] || type
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Cobranças'
        grid.subtitle = ''
        grid.showActs = false
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Método', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Parcelamento', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnEdit = new GridButton({
                callback: () => editEntity(item.paymentLinkId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.purchaseDescription })
            line.slots.push({ content: friendlyType(item.purchaseType) })
            let type = '-'
            if (item.purchaseType !== PurchaseType.recurring_continuous) {
                type = `até ${item.purchaseTotalInstallments}x`
            }
            line.slots.push({ content: type })
            line.slots.push({ content: item.purchaseTotalValue })
            if (window.store.token.permissions.pay.links.write) {
                line.slots.push({ content: [btnEdit] })
            } else {
                line.slots.push({ content: [] })
            }

            grid.lines.push(line)
        }
    }

    /**
     * Este campo significa duas coisas conforme o tipo, e é essa mistura que causava o bug.
     *
     * No pagamento único é o teto de PARCELAS — dividir uma transação, coisa que só o cartão faz.
     * No carnê é a quantidade de VENCIMENTOS, e aí vale para qualquer meio: é justamente assim que
     * pix e boleto fracionam uma venda.
     *
     * Travar por "é boleto" zerava o carnê no boleto, transformando 6 vencimentos numa cobrança só.
     */
    function semParcelamento(purchaseType: PurchaseType, method: string): boolean {
        if (purchaseType === PurchaseType.recurring_continuous) return true
        return purchaseType === PurchaseType.full_charge && method !== PaymentMethods.credit
    }

    $: {
        if (semParcelamento(entity.purchaseType, entity.paymentMethodsAllowed)) {
            entity.purchaseTotalInstallments = 1
        }
    }

    // Entrada é conceito de carnê: o primeiro vencimento vale a entrada e os demais dividem o resto.
    // Fica atrás de um toggle porque desligada a divisão é a de sempre, que é o caso comum.
    let comEntrada = false
    $: temEntrada = entity.purchaseType === PurchaseType.recurring_fixed

    // O texto segue a convenção de vitrine: a entrada é anunciada à parte e o número mostrado é o
    // de parcelas DEPOIS dela. Por dentro continuam sendo N vencimentos, com o primeiro ajustado.
    $: resumoDaEntrada = (() => {
        if (!temEntrada || !comEntrada) return ''
        const entrada = entity.purchaseEntryValue
        const vencimentos = entity.purchaseTotalInstallments || 1
        if (!(entrada > 0) || entrada >= entity.purchaseTotalValue || vencimentos < 2) return ''
        const restante = (entity.purchaseTotalValue - entrada) / (vencimentos - 1)
        const dinheiro = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        return `Entrada de ${dinheiro(entrada)} + ${vencimentos - 1}x de ${dinheiro(restante)}`
    })()

    // Tipos aceitos pela combinação gateway + meio de pagamento escolhidos. Vem da api: o Mercado
    // Pago, por exemplo, só faz cobrança convencional, então recorrente nem aparece como opção.
    // Sem meio definido, mostra a união do que o gateway aceita em algum meio.
    //
    // `list` é parâmetro (e não a variável de cima) para que o cálculo reativo enxergue os gateways
    // como dependência — o Svelte não olha dentro da função para montar a lista de dependências.
    function purchaseTypeOptions(gatewayId: string, method: string, list: Gateway[]) {
        const found = list.find((el) => el.gatewayId === gatewayId)
        const mapa = found?.gatewayPurchaseTypes || {}
        const tipos = method ? mapa[method] || [] : [...new Set(Object.values(mapa).flat())]
        return tipos.map((tipo) => ({ value: tipo, label: PurchaseTypeFriendly[tipo] || tipo }))
    }

    // Meios aceitos pelo gateway escolhido, já com o rótulo de exibição. Vem da api — antes as
    // opções eram fixas no template, então um meio novo (pix, por exemplo) não aparecia mesmo com
    // o gateway aceitando, e sem erro nenhum para denunciar.
    function paymentMethodOptions(gatewayId: string, list: Gateway[]) {
        const found = list.find((el) => el.gatewayId === gatewayId)
        return (found?.gatewayPaymentMethods || []).map((method) => ({
            value: method,
            label: PaymentMethodsFriendly[method] || method,
        }))
    }

    // opção vazia explícita: sem ela o select fica sem seleção visível enquanto o valor está vazio
    $: gatewayOptions = [{ value: null, label: '' }, ...gateways.map((item) => ({ value: item.gatewayId, label: item.label }))]
    $: meioOptions = [{ value: '', label: '' }, ...paymentMethodOptions(entity.gatewayId, gateways)]
    // Cobrança é o que vende um total: à vista/parcelado no cartão, ou carnê. A assinatura
    // (recorrente contínua) tem cadastro próprio. Ver isSubscriptionType().
    $: tipoOptions = [
        { value: '', label: '' },
        ...purchaseTypeOptions(entity.gatewayId, entity.paymentMethodsAllowed, gateways).filter((t) => !isSubscriptionType(t.value)),
    ]
</script>

{#if step === 1}
    <Content footer={true}>
        <Zact>
            <div class="spacer" />
            {#if window.store.token.permissions.pay.links.write}
                <Zbutton color="primary" icon={faPlusCircle} label="Cadastrar nova cobrança" mobileHideLabel={true} on:click={newEntity} />
            {/if}
        </Zact>
        <Zgrid class="h-space" {grid} />
        <LoadSpinner {show} />
    </Content>
{/if}
{#if step === 2}
    <Content footer={true}>
        <Zact>
            {#if !form}
                <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showEntities} />
            {/if}
            <div class="spacer" />
            {#if window.store.token.permissions.pay.links.write}
                {#if !!entity.paymentLinkId}
                    <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={show} on:click={removeEntity} />
                {/if}
                <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={saveEntity} />
            {/if}
        </Zact>
        <Zframe title="Cobrança" class="h-space">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput size={6} sizeM={12} label="Descrição da compra" type="text" bind:value={entity.purchaseDescription} />
                <Zinput size={6} sizeM={12} label={purchaseTotalLabel} type="number" bind:value={entity.purchaseTotalValue} />
                <ZinputTitle title="Gateway"></ZinputTitle>
                <Zinput size={4} sizeM={12} label="Gateway de pagamento" type="select" options={gatewayOptions} bind:value={entity.gatewayId} />
                <Zinput size={4} sizeM={12} label="Meio de pagamento" type="select" options={meioOptions} bind:value={entity.paymentMethodsAllowed} />
                <Zinput size={4} sizeM={12} label="Método de cobrança" type="select" options={tipoOptions} bind:value={entity.purchaseType} />
                <Zinput
                    size={4}
                    sizeM={12}
                    label={entity.purchaseType === PurchaseType.recurring_fixed ? 'Vencimentos' : 'Parcelamento máximo'}
                    type="number"
                    disabled={semParcelamento(entity.purchaseType, entity.paymentMethodsAllowed)}
                    bind:value={entity.purchaseTotalInstallments}
                />
                {#if temEntrada}
                    <!-- desligado, a divisão é a de sempre: o total dividido igualmente -->
                    <Zinput size={4} sizeM={12} label="Pedir Entrada" type="toggle" bind:value={comEntrada} />
                    {#if comEntrada}
                        <Zinput size={4} sizeM={12} label="Valor da entrada" type="number" bind:value={entity.purchaseEntryValue} />
                    {/if}
                {/if}
            </ZContainer>
            {#if resumoDaEntrada}
                <div class="alert alert-secondary mt-3 mb-0">{resumoDaEntrada}</div>
            {/if}
        </Zframe>
        <LoadSpinner {show} />
    </Content>
{/if}
